import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthContext, { AuthContextType } from "src/app/contexts/auth-context";
import { useAuth } from "src/app/features/auth/hooks/useAuth";
import { getAuthClaimsEntity } from "src/app/utils";
import {
  User,
  useGetUserByEmailLazyQuery,
  useGetUserMhcRolesLazyQuery,
  useGetUserPermissionsLazyQuery,
} from "src/generated/graphql";
import { MHCRoles } from "src/app/providers/permissions/mhc-roles";

export const AuthContextProvider = ({ children }) => {
  const auth = useAuth();
  const location = useLocation();

  const isAuthRoute = location.pathname === "/auth";
  const navigate = useNavigate();
  const [authContextData, setAuthContextData] = useState<AuthContextType>();
  const { refreshToken, accessToken } = authContextData ?? {};

  const [
    getUserByEmail,
    { data: userData, loading: userLoading, error: userFetchError },
  ] = useGetUserByEmailLazyQuery({
    fetchPolicy: "network-only",
  });
  const [getUserPermissions, { data: userPermissionsData }] =
    useGetUserPermissionsLazyQuery();
  const [getUserMHCRoles, { data: userMHCRolesData }] =
    useGetUserMhcRolesLazyQuery();

  const handlePermissionsData = useCallback(
    async (permissionData: string[], userData: User) => {
      localStorage.setItem(
        `userMHCPermissions`,
        JSON.stringify(permissionData)
      );
    },
    []
  );

  useEffect(() => {
    if (userPermissionsData?.userPermissions?.length) {
      handlePermissionsData(
        userPermissionsData?.userPermissions,
        userData?.userByEmail as User
      );
    }
  }, [
    userPermissionsData?.userPermissions,
    handlePermissionsData,
    userData?.userByEmail,
  ]);

  useEffect(() => {
    const roles = userMHCRolesData?.userRoles ?? [];
    if (roles.length > 0) {
      localStorage.setItem(`userMHCRoles`, JSON.stringify(roles));
    } else {
      localStorage.removeItem(`userMHCRoles`);
    }
  }, [userMHCRolesData?.userRoles]);

  const getLoggedInUser = useCallback(
    (email?) => {
      let _email: string = email;
      if (!_email) {
        const accessToken = localStorage.getItem("token");
        const refreshToken = localStorage.getItem("refresh_token");

        if (accessToken && refreshToken) {
          _email = (getAuthClaimsEntity(accessToken)?.email as string) ?? "";
        } else if (!authContextData?.authLoaded) {
          setTimeout(() => {
            setAuthContextData?.((_auth) => {
              return {
                ...(authContextData ?? {}),
                authLoaded: true,
              } as AuthContextType;
            });
          }, 300);
        }
      }
      getUserByEmail({
        variables: {
          email: _email,
        },
      });
    },
    [getUserByEmail, authContextData]
  );

  const resetAuthContext = useCallback(
    (email: string) => {
      setAuthContextData((_data) => ({ ..._data } as AuthContextType));
      getLoggedInUser(email);
    },
    [setAuthContextData, getLoggedInUser]
  );

  const authValue = useMemo(
    () => ({
      authContextData,
      setAuthContextData,
      getLoggedInUser,
      resetAuthContext,
    }),
    [authContextData, setAuthContextData, getLoggedInUser, resetAuthContext]
  );

  const isTokenExpired = useCallback((token) => {
    const decodedToken = getAuthClaimsEntity(token);
    let expired = (decodedToken?.exp ?? 0) * 1000 < Date.now() / 1000;
    return expired;
  }, []);

  useEffect(() => {
    getLoggedInUser();
  }, [getLoggedInUser]);

  useEffect(() => {
    const interval = setInterval(() => {
      const accessToken = localStorage.getItem("token");
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken && accessToken) {
        if (isTokenExpired(authContextData?.accessToken)) {
          auth.refreshAccessToken(refreshToken).then((response) => {
            if (response?.access_token && response?.refresh_token) {
              localStorage.setItem("token", response?.access_token);
              localStorage.setItem("refresh_token", response?.refresh_token);
              setAuthContextData({
                ...(authContextData ?? {}),
                accessToken: response?.access_token,
                refreshToken: response?.refresh_token,
              });
            } else {
             
              auth.logout();
              navigate("login");
            }
          });
        }
      }
    }, 15 * 60 * 1000);

    return () => clearInterval(interval);
  }, [
    setAuthContextData,
    auth.refreshAccessToken,
    auth.logout,
    auth,
    authContextData,
    isTokenExpired,
    navigate,
  ]);

  useEffect(() => {
    const _data = {
      ...(authContextData ?? {}),
      currentUser: userData?.userByEmail as User,
    } as AuthContextType;

    if (
      !userLoading &&
      userData?.userByEmail?.id &&
      JSON.stringify(_data) != JSON.stringify(authContextData)
    ) {
      getUserPermissions();
      getUserMHCRoles();
      localStorage.setItem("currentUser", JSON.stringify(userData.userByEmail));
      setAuthContextData?.((_authContextData) => {
        return _data;
      });
      setTimeout(() => {
        if (isAuthRoute) navigate("/landing");
      }, 300);
    }

    if (userFetchError?.graphQLErrors?.length ?? 0 > 0) {
     
      auth.logout();

      navigate("/login");
    }
  }, [
    userLoading,
    userData?.userByEmail,
    userFetchError?.graphQLErrors,
    navigate,
    isAuthRoute,
    auth,
    authContextData,
    getUserPermissions,
    getUserMHCRoles,
  ]);

  // Deny access once MHC roles resolve and the user holds none of the allowed roles.
  useEffect(() => {
    const userRoles = userMHCRolesData?.userRoles;
    if (!userLoading && userData?.userByEmail?.id && userRoles !== undefined) {
      const allowedRoles = Object.values(MHCRoles);
      const hasAllowedRole =
        Array.isArray(userRoles) &&
        userRoles.some((role) => allowedRoles.includes(role));
      if (!hasAllowedRole) {
        setTimeout(() => {
          navigate("/access-denied");
        }, 100);
      }
    }
  }, [
    userLoading,
    userData?.userByEmail?.id,
    userMHCRolesData?.userRoles,
    navigate,
  ]);

  return (
    <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
  );
};
